import { useState } from 'react';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { updateProfile, uploadAvatar, getAvatarUrl } from '@ego/api';
import styles from './EditProfileModal.module.css';

export default function EditProfileModal({ profile, onClose, onSaved }) {
  const [name, setName] = useState(profile.name || '');
  const [bio, setBio] = useState(profile.bio || '');
  const [website, setWebsite] = useState(profile.website || '');
  const [websiteUrl, setWebsiteUrl] = useState(profile.websiteUrl || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState(getAvatarUrl(profile.avatarPath));
  const [loading, setLoading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const applySelectedFile = (file) => {
    if (!file) return;
    setAvatarFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleFileChange = (e) => {
    applySelectedFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please drop an image file.');
      return;
    }
    applySelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updates = {
        name: name.trim(),
        bio: bio.trim(),
        website: website.trim(),
        website_url: websiteUrl.trim(),
      };
      if (avatarFile) {
        updates.avatar_path = await uploadAvatar(avatarFile);
      }
      await updateProfile(profile.id, updates);
      toast.success('Profile updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        <h2 className={styles.heading}>Edit profile</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.avatarSection}>
            <label
              className={`${styles.avatarTrigger} ${isDragActive ? styles.avatarTriggerDrag : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (!isDragActive) setIsDragActive(true);
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                setIsDragActive(true);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setIsDragActive(false);
              }}
              onDrop={handleDrop}
            >
              <div className={styles.avatarPreview}>
                {preview ? (
                  <img src={preview} alt="Avatar" className={styles.avatarImg} />
                ) : (
                  <div className={styles.avatarPlaceholder} />
                )}
                <span className={styles.avatarOverlay}>Change</span>
              </div>
              <span className={styles.avatarBtn}>Change profile pic</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInputOverlayArea}
              />
            </label>
            <span className={styles.dropHint}>or drag an image file here</span>
          </div>
          {profile.handle && (
            <div className={styles.label}>
              Username
              <div className={styles.readonlyField}>@{profile.handle}</div>
              <span className={styles.readonlyHint}>Username cannot be changed</span>
            </div>
          )}
          <label className={styles.label}>
            <span className={styles.requiredMark}>Name</span>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={styles.input}
              required
              autoFocus
            />
          </label>
          <label className={styles.label}>
            Bio
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              className={styles.textarea}
              rows={3}
              placeholder="Add a bio..."
            />
          </label>
          <label className={styles.label}>
            Website name
            <input
              type="text"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              className={styles.input}
              placeholder="e.g. seanxthielen.com"
            />
          </label>
          <label className={styles.label}>
            Website URL
            <input
              type="url"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              className={styles.input}
              placeholder="https://..."
            />
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
